import { toApiProblem } from './client'

/**
 * A rejected form, split into what belongs under each field and what belongs at the top.
 *
 * <p>The server has always sent every invalid field by name — `errors: [{ field, message }]` —
 * and the dialogs showed only the problem's headline, "One or more fields are invalid". A
 * person who typed INDIAN as a nationality was told something was wrong and not what, and
 * had nine fields to guess between.
 */
export interface FormProblem {
  /** Field name, as the request body spells it, to the message for that field. */
  fields: Record<string, string>
  /** For the line at the top of the form — the specific detail, or a pointer to the fields. */
  summary: string
}

/**
 * @param options.keepPath keep the whole field path (`items[2].amount`) instead of its last
 *   segment. A form with one box per field wants `amount`; a form with a row per item needs the
 *   index, or every row's message lands on the same key and only the first survives.
 */
export function formProblem(
  error: unknown, fallback: string, options: { keepPath?: boolean } = {},
): FormProblem {
  const problem = toApiProblem(error)
  const fields: Record<string, string> = {}
  for (const fieldError of problem?.errors ?? []) {
    // The first message per field: a field that fails two rules needs fixing once, and two
    // sentences under one box read as two problems.
    const key = options.keepPath ? fieldError.field : fieldError.field.replace(/^.*\./, '')
    if (key && !(key in fields)) fields[key] = fieldError.message
  }
  const count = Object.keys(fields).length
  return {
    fields,
    summary: count > 0
      ? count === 1
        ? 'One field needs attention — see the message under it.'
        : `${count} fields need attention — see the messages under them.`
      : problem?.detail ?? fallback,
  }
}
